import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Home, FileText, Download, Eye, ExternalLink } from 'lucide-react';

// 의사결정 트리 데이터 구조
interface DecisionNode {
  id: string;
  type: 'question' | 'result';
  title: string;
  content: string;
  options?: {
    text: string;
    nextId: string;
  }[];
  images?: string[];
  table?: {
    headers: string[];
    rows: string[][];
    mergedCells?: {
      row: number;
      col: number;
      rowspan?: number;
      colspan?: number;
    }[];
  };
  links?: {
    text: string;
    url: string;
  }[];
  nextId?: string;
}

// 의사결정 트리 데이터
const decisionTreeData: Record<string, DecisionNode> = {
  start: {
    id: 'start',
    type: 'question',
    title: '보수교육·면허신고 절차 안내 도우미',
    content: '보수교육 및 면허신고에 대한 정확한 안내를 위해\n먼저, 보수교육 이수 현황과 면제·유예·비대상 신청 및 면허신고 여부 상태를 확인해 보세요.\n\n현재 본인의 현황을 잘 모르는 경우,\n아래 두 조회 경로를 먼저 확인한 후 보수교육·면허신고 절차 안내 도우미를 시작해 주세요.',
    links: [{
      text: '▶ 보수교육 이수 현황 조회: 협회 홈페이지 → 마이페이지 → 교육이수현황',
      url: 'https://www.khima.or.kr/'
    }, {
      text: '▶ 면제·유예·비대상 / 면허신고 여부 조회: 면허신고센터 → 신고현황확인',
      url: 'https://lic.kuksiwon.or.kr/mypage/login.do?jj_code=14'
    }, {
      text: '▶ 보수교육·면허신고 관련 FAQ',
      url: '#/faq',
      download: false
    }],
    options: [{
      text: '시작하기',
      nextId: 'Q1'
    }]
  },
  Q1: {
    id: 'Q1',
    type: 'question',
    title: '질문 1',
    content: '최근 3년이내 면허신고를 하셨나요?',
    options: [{
      text: 'YES',
      nextId: 'A1'
    }, {
      text: 'NO',
      nextId: 'A2'
    }]
  },
  Q2: {
    id: 'Q2',
    type: 'question',
    title: '질문 2',
    content: '면허신고 또는 보수교육 면제·유예·비대상 신청을 위해, 신청 대상 연도에 보건의료정보관리사 업무를 총 6개월 이상 종사한 기간이 있나요?\n\n※ 보건의료정보관리사(의무기록사) 업무\n\n6개월 이상 아래 보건의료정보관리사(의무기록사) 업무 중 하나의 직무라도 수행하는 경우 보수교육을 이수하여야 합니다.',
    images: ['images/면허업무내용2.jpeg'],
    options: [{
      text: 'YES',
      nextId: 'A3'
    }, {
      text: 'NO',
      nextId: 'A4'
    }]
  },
  A1: {
    id: 'A1',
    type: 'result',
    title: '면허신고 미대상자',
    content: '면허신고 \'미대상자\' 입니다. 차기 신고 기간에 맞추어 면허신고 진행하여 주시기 바랍니다.',
    nextId: 'X1'
  },
  A2: {
    id: 'A2',
    type: 'result',
    title: '면허신고 대상자',
    content: '',
    nextId: 'Q2'
  },
  Q3: {
    id: 'Q3',
    type: 'question',
    title: '질문 3',
    content: '보수교육을 이수하였나요?',
    options: [{
      text: 'YES',
      nextId: 'A5'
    }, {
      text: 'NO',
      nextId: 'A6'
    }]
  },
  Q4: {
    id: 'Q4',
    type: 'question',
    title: '질문 4',
    content: '보수교육 면제·유예·비대상 신청 대상 연도를 선택해주세요.\n\n※ 보건의료정보관리사(의무기록사) 업무\n\n6개월 이상 아래 보건의료정보관리사(의무기록사) 업무 중 하나의 직무라도 수행하는 경우 보수교육을 이수하여야 합니다.',
    images: ['images/면허업무내용2.jpeg'],
    options: [{
      text: '2014년~2016년',
      nextId: 'A7'
    }, {
      text: '2017년~2018년',
      nextId: 'Q5'
    }, {
      text: '2019년 이후',
      nextId: 'A8'
    }]
  },
  Q5: {
    id: 'Q5',
    type: 'question',
    title: '질문 5',
    content: '신청대상연도에 보건의료정보관리사 업무 미종사 이후 1번 이상 보건의료정보관리사 업무에 종사하였나요?',
    options: [{
      text: 'YES',
      nextId: 'A9'
    }, {
      text: 'NO',
      nextId: 'A10'
    }]
  },
  A3: {
    id: 'A3',
    type: 'result',
    title: '보수교육 이수 대상자',
    content: '보수교육 \'이수 대상자\' 입니다.\n\n보건의료정보관리사는 해당연도에 면허 업무를 6개월 이상 종사하는 경우 해당연도에 8평점의 보수교육을 이수해야 합니다.\n\n다만, 다음과 같이 비대상(유예)자로 판정 받은 이후 보건의료정보관리사 면허 업무 종사기간이 6개월 이상인 연도에 아래와 같이 의무 보수교육 이수 시간이 발생됩니다.',
    table: {
      headers: ['비대상(유예)자 판정 기간', '의무 보수교육 이수 시간'],
      rows: [['1년 이상 2년 미만(1개년도 비대상(유예)자 판정)', '12시간'], ['2년 이상 3년 미만(2개년도 비대상(유예)자 판정)', '16시간'], ['3년 이상(3개년도 이상 비대상(유예)자 판정)', '20시간']]
    },
    content2: '\n▶ 보수교육 필수과목 이수 안내',
    table2: {
      headers: ['구분', '내용'],
      rows: [['필수과목이란?', '면허신고 대상기간 동안 보건의료정보관리사 면허 업무에 종사한 경우 반드시 이수해야 하는 보수교육 2시간 과정\n(매년 2시간을 이수하는 것이 아닌 면허신고 대상연도 보수교육 이수 시간 중 2시간만 이수)'], ['왜 필요한가?', '2021년 면허신고부터 관련 업무 종사자는 면허신고 대상연도 보수교육 이수 시간 중 필수과목 2시간을 포함해야 면허신고 가능'], ['언제 적용되는가?', '최근 3년 면허신고 대상기간 중 1년 이상 관련 업무 종사 시 필수과목 이수 의무 적용']]
    },
    nextId: 'Q3'
  },
  A4: {
    id: 'A4',
    type: 'result',
    title: '보수교육 면제·유예·비대상 신청 대상자',
    content: '보수교육 \'면제·유예·비대상 신청 대상자\' 입니다.\n\n○ 보수교육 면제·유예·비대상자 안내',
    table: {
      headers: ['구분', '2014년~2016년', '2017년~2018년', '2019년부터~'],
      rows: [['보수교육\n면제대상', '의료기관 등에서 보건의료정보관리사 업무에 종사한 기간이 6개월 미만인 자', '의료기관 등에서 보건의료정보관리사 업무에 종사한 기간이 6개월 미만인 자', '-'], ['', '군 복무중인 자', '군 복무중인 자', '군 복무중인 자'], ['', '해당 전공 관련 대학원 재학생', '해당 전공 관련 대학원 재학생', '해당 전공 관련 대학원 재학생'], ['', '신규면허취득자', '신규면허취득자', '신규면허취득자'], ['', '보건복지부장관이 보수교육을 받을 필요가 없다고 인정하는 자', '보건복지부장관이 보수교육을 받을 필요가 없다고 인정하는 자', '보건복지부장관이 보수교육을 받을 필요가 없다고 인정하는 자'], ['보수교육\n유예대상', '보수교육 대상이나 본인의 질병이나 그 밖의 불가피한 사유로 보수교육을 받기가 곤란하다고 인정하는 자', '의료기관 등에 소속되어 있으면서 입원 치료중인 자', '보건의료정보관리사 업무 미종사기간이 6개월 이상인 자'], ['', '', '외국에서 해당 면허 범위 내 업무에 종사하면서 보수교육을 이수받지 못한 자', '보건복지부장관이 보수교육을 받기 어렵다고 인정하는 자'], ['', '', '해당연도(1월1일~12월31일까지) 육아휴직', ''], ['보수교육\n비대상', '-', '해당연도 보건의료정보관리사 업무에 종사한 적이 없는 자', '-'], ['', '', '보건의료정보관리사 업무에 종사한 기간이 6개월 미만 종사자면서 1년 안에 업무 미복귀자', '']],
      mergedCells: [{
        row: 0,
        col: 0,
        rowspan: 5
      }, {
        row: 5,
        col: 0,
        rowspan: 3
      }, {
        row: 8,
        col: 0,
        rowspan: 2
      }]
    },
    nextId: 'Q4'
  },
  A5: {
    id: 'A5',
    type: 'result',
    title: '면허신고센터 통합 로그인',
    content: '(면허신고 요건*을 충족한 경우) 면허신고센터 통합 로그인\n*면허신고 요건 : 보수교육 이수 내역 or 면제·유예·비대상 판정 내역',
    links: [{
      text: '면허신고센터 바로가기',
      url: 'https://lic.kuksiwon.or.kr'
    }],
    images: ['images/A1-login1_httpsworks.doGigpdEQ.jpeg', 'images/A1-login2_httpsworks.doxvF3oNH.jpeg'],
    nextId: 'B1'
  },
  A6: {
    id: 'A6',
    type: 'result',
    title: '보수교육 이수현황 확인',
    content: '[협회 홈페이지]-[마이페이지]-[교육 이수현황]에서 보수교육 이수현황 확인\n\n①협회 홈페이지 로그인 후 마이페이지 접속\n↓\n② \'교육 이수현황\' 탭 클릭\n↓\n③ 연도별 선택하여 보수교육 이수현황 확인',
    links: [{
      text: '협회 홈페이지 바로가기',
      url: 'https://www.khima.or.kr/'
    }],
    images: ['images/A6-status1_httpsworks.doxah5q6q.png'],
    nextId: 'D1'
  },
  A7: {
    id: 'A7',
    type: 'result',
    title: '보수교육 면제자 (2014년~2016년)',
    content: '\'보수교육 면제자\' 입니다.\n\n○ 신청 요건 및 증빙서류 (2014년 ~ 2016년 신청 시)',
    links: [{
      text: '서식 다운로드',
      url: 'https://www.khima.or.kr/board/board.php?bo_table=01notice&wr_id=1606&sca=%EB%A9%B4%ED%97%88%EC%8B%A0%EA%B3%A0'
    }],
    images: ['images/2014년~2016년 신청 증빙서류_.jpg'],
    nextId: 'C1'
  },
  A8: {
    id: 'A8',
    type: 'result',
    title: '보수교육 유예자 (2019년 이후)',
    content: '\'보수교육 유예자\' 입니다.\n\n○ 신청 요건 및 증빙서류 (2019년부터 신청 시)',
    links: [{
      text: '서식 다운로드',
      url: 'https://www.khima.or.kr/board/board.php?bo_table=01notice&wr_id=1606&sca=%EB%A9%B4%ED%97%88%EC%8B%A0%EA%B3%A0'
    }],
    images: ['images/2019년 이후_.jpg'],
    nextId: 'C1'
  },
  A9: {
    id: 'A9',
    type: 'result',
    title: '보수교육 면제자 (2017년~2018년)',
    content: '\'보수교육 면제자\' 입니다.\n\n○ 신청 요건 및 증빙서류 (2017년 ~ 2018년 신청 시)',
    links: [{
      text: '서식 다운로드',
      url: 'https://www.khima.or.kr/board/board.php?bo_table=01notice&wr_id=1606&sca=%EB%A9%B4%ED%97%88%EC%8B%A0%EA%B3%A0'
    }],
    images: ['images/2017~2018년 증빙서류_면제유예_.jpg'],
    nextId: 'C1'
  },
  A10: {
    id: 'A10',
    type: 'result',
    title: '보수교육 비대상자 (2017년~2018년)',
    content: '\'보수교육 비대상자\' 입니다.\n\n○ 신청 요건 및 증빙서류 (2017년 ~ 2018년 신청 시)',
    images: ['images/2017~2018년 증빙서류_비대상자_.jpg'],
    nextId: 'C1'
  },
  B1: {
    id: 'B1',
    type: 'result',
    title: '면허신고서 작성',
    content: '면허신고서 작성',
    images: ['images/B1 - write1_httpsworks.doxzulvGv.jpeg'],
    nextId: 'B2'
  },
  B2: {
    id: 'B2',
    type: 'result',
    title: '면허신고 절차 완료',
    content: '면허신고 절차 완료',
    images: ['images/B2-check1_httpsworks.do5Imi2nc.jpeg', 'images/B2-check2_httpsworks.doGQFSBLJ.jpeg'],
    nextId: 'B3'
  },
  B3: {
    id: 'B3',
    type: 'result',
    title: '면허신고 최종 결과 확인',
    content: '면허신고 최종 결과 확인\n\n① \'신고현황확인\' 탭 클릭\n↓\n② \'면허신고 현황\' 내역에서 \'신고결과\' 확인\n↓\n③(필요시)신고확인서출력',
    images: ['images/B3 - final1_httpsworks.do56oRnUp.jpeg', 'images/B3 - final2_httpsworks.doFAm2UXY.jpeg'],
    nextId: 'X1'
  },
  C1: {
    id: 'C1',
    type: 'result',
    title: '증빙서류 준비',
    content: '면제·유예·비대상 사유별 증빙서류 준비',
    links: [{
      text: '증빙서류 바로가기',
      url: 'https://www.khima.or.kr/board/board.php?bo_table=01notice&wr_id=1606'
    }],
    nextId: 'C2'
  },
  C2: {
    id: 'C2',
    type: 'result',
    title: '면제·유예·비대상 신청서 작성',
    content: '면제·유예·비대상 신청서 작성\n\n① \'면제·유예·비대상자\' 탭 클릭\n↓\n② 개인정보수집 및 이용 동의\n↓\n③ 면제·유예·비대상자 신청서 작성',
    links: [{
      text: '보수교육 면제·유예·비대상 신청 바로가기',
      url: 'https://lic.kuksiwon.or.kr/mypage/login.do?jj_code=14'
    }],
    images: ['images/C2- write1_httpsworks.do5tVJa0T.jpeg'],
    nextId: 'C3'
  },
  C3: {
    id: 'C3',
    type: 'result',
    title: '면제·유예·비대상 신청 심사 결과 확인',
    content: '면제·유예·비대상 신청 심사 결과 확인\n\n① \'신고현황확인\' 탭 클릭 후 \'면제·유예·비대상 신청 현황\' 내역에서 \'판정결과\' 확인\n↓\n② (면제/유예 불가자 판정 시) \'면제·유예불가사유\' 버튼 클릭하여 불가 사유 확인\n↓\n③ (면제/유예 불가자 판정 시) \'서류 수정\' 버튼 클릭 → 신청내역확인 화면에서 \'수정\' 버튼 클릭하여 증빙서류 보완',
    links: [{
      text: '면제·유예·비대상 신청내역 확인하러 바로가기',
      url: 'https://lic.kuksiwon.or.kr/mypage/login.do?jj_code=14'
    }],
    images: ['images/C3 - reason1_httpsworks.dox7MnWis.png', 'images/C3 - reason2_httpsworks.doxVXB58k.jpeg', 'images/C3 - reason3_httpsworks.doG9NrG6c.jpeg', 'images/C3-reason4_httpsworks.doFFka0aH.jpeg'],
    nextId: 'A5'
  },
  D1: {
    id: 'D1',
    type: 'result',
    title: '보수교육 이수',
    content: '보수교육 이수 현황을 확인한 후, 부족한 평점은 협회 온라인교육센터에서 제공하는 상시 온라인 보수교육을 통해 이수하실 수 있습니다.\n\n◎ 접속방법 ①:[협회홈페이지] 접속 → 홈페이지 상단의 \'온라인교육\' 클릭하여 접속\n◎ 접속방법 ②:[협회홈페이지] 접속 → 교육센터 메뉴 클릭 → \'온라인교육\' 클릭하여 접속',
    links: [{
      text: '◉ 온라인교육센터 바로가기',
      url: 'https://khima.hullaro.com/userMain/goUserMain'
    }],
    images: ['images/D1-online1_httpsworks.doFKc0SY3.jpeg', 'images/D1-online2_httpsworks.doFT1MFPB.jpeg'],
    nextId: 'A5'
  },
  X1: {
    id: 'X1',
    type: 'result',
    title: '안내 완료',
    content: '안내가 종료되었습니다.\n\n추가 문의사항이 있으시면 대한보건의료정보관리사협회로 문의하여 주시기 바랍니다.',
    links: [{
      text: '대한보건의료정보관리사협회 홈페이지',
      url: 'https://www.khima.or.kr/'
    }]
  }
};
interface DecisionTreeProps {
  onShowAdmin?: () => void;
}
export default function DecisionTree({
  onShowAdmin
}: DecisionTreeProps) {
  const [currentNodeId, setCurrentNodeId] = useState('start');
  const [history, setHistory] = useState<string[]>(['start']);
  const [stepNumber, setStepNumber] = useState(1);

  // URL 파라미터에서 admin 모드 확인
  const [isAdminMode, setIsAdminMode] = useState(false);
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setIsAdminMode(urlParams.get('admin') === 'true');
  }, []);
  const currentNode = decisionTreeData[currentNodeId];
  const handleOptionClick = (nextId: string) => {
    setHistory(prev => [...prev, nextId]);
    setCurrentNodeId(nextId);
    setStepNumber(prev => prev + 1);
  };
  const handleNext = () => {
    if (currentNode.nextId) {
      handleOptionClick(currentNode.nextId);
    }
  };
  const handleBack = () => {
    if (history.length > 1) {
      const newHistory = history.slice(0, -1);
      setHistory(newHistory);
      setCurrentNodeId(newHistory[newHistory.length - 1]);
      setStepNumber(prev => prev - 1);
    }
  };
  const handleHome = () => {
    setCurrentNodeId('start');
    setHistory(['start']);
    setStepNumber(1);
  };
  const handlePrint = () => {
    window.print();
  };
  const renderNestedTable = (nestedTable: {
    headers: string[];
    rows: string[][];
    mergedCells?: {
      row: number;
      col: number;
      rowspan?: number;
      colspan?: number;
    }[];
  }) => {
    const mergedCells = nestedTable.mergedCells || [];
    const isMerged = (row: number, col: number) => {
      return mergedCells.some(merged => {
        const rowStart = merged.row;
        const rowEnd = merged.row + (merged.rowspan || 1) - 1;
        const colStart = merged.col;
        const colEnd = merged.col + (merged.colspan || 1) - 1;
        return row >= rowStart && row <= rowEnd && col >= colStart && col <= colEnd && !(row === merged.row && col === merged.col);
      });
    };
    const getMergedCell = (row: number, col: number) => {
      return mergedCells.find(merged => merged.row === row && merged.col === col);
    };
    return <table className="nested-table w-full border-collapse border border-gray-300">
        <tbody>
          {nestedTable.rows.map((row, rowIndex) => <tr key={rowIndex}>
              {row.map((cell, cellIndex) => {
            if (isMerged(rowIndex, cellIndex)) {
              return null;
            }
            const mergedCell = getMergedCell(rowIndex, cellIndex);
            const rowspan = mergedCell?.rowspan || 1;
            const colspan = mergedCell?.colspan || 1;
            return <td key={cellIndex} className="border border-gray-300 p-2 text-center text-sm whitespace-pre-line" rowSpan={rowspan} colSpan={colspan}>
                    {cell}
                  </td>;
          })}
            </tr>)}
        </tbody>
      </table>;
  };
  const renderTable = (table: {
    headers: string[];
    rows: string[][];
    mergedCells?: {
      row: number;
      col: number;
      rowspan?: number;
      colspan?: number;
    }[];
  }) => {
    const mergedCells = table.mergedCells || [];
    const isMerged = (row: number, col: number) => {
      return mergedCells.some(merged => {
        const rowStart = merged.row;
        const rowEnd = merged.row + (merged.rowspan || 1) - 1;
        const colStart = merged.col;
        const colEnd = merged.col + (merged.colspan || 1) - 1;
        return row >= rowStart && row <= rowEnd && col >= colStart && col <= colEnd && !(row === merged.row && col === merged.col);
      });
    };
    const getMergedCell = (row: number, col: number) => {
      return mergedCells.find(merged => merged.row === row && merged.col === col);
    };
    return <div className="overflow-x-auto my-6">
        <table className="gov-table">
          <thead>
            <tr>
              {table.headers.map((header, index) => <th key={index} className="text-center p-4 font-semibold" style={{
              textAlign: 'center'
            }}>{header}</th>)}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => <tr key={rowIndex} className="border-b border-gray-200 hover:bg-gray-50">
                {row.map((cell, cellIndex) => {
              if (isMerged(rowIndex, cellIndex)) {
                return null;
              }
              const mergedCell = getMergedCell(rowIndex, cellIndex);
              const rowspan = mergedCell?.rowspan || 1;
              const colspan = mergedCell?.colspan || 1;
              return <td key={cellIndex} className="p-4 align-middle whitespace-pre-line text-center" style={{
                textAlign: 'center'
              }} rowSpan={rowspan} colSpan={colspan}>
                      {typeof cell === 'object' && cell.type === 'nested-table' ? renderNestedTable(cell.data) : <span className={typeof cell === 'string' && (cell.includes('○') || cell.includes('■')) ? 'emphasis' : ''}>{cell}</span>}
                    </td>;
            })}
              </tr>)}
          </tbody>
        </table>
      </div>;
  };
  const renderImages = (images: string[]) => {
    const getImageDescription = (image: string, index: number) => {
      if (image.includes('A1-login1')) return '① 면허신고센터 사이트 접속';
      if (image.includes('A1-login2')) return '② 면허자격정보 입력하여 로그인';
      if (image.includes('A6-status1')) return '[마이페이지] - 교육이수현황 확인 페이지';
      if (image.includes('D1-online1')) return '온라인교육센터 사이트 접속 위치';
      if (image.includes('D1-online2')) return '온라인교육센터 화면';
      if (image.includes('B1 - write1')) return '면허신고서 작성 화면';
      if (image.includes('B2-check1')) return '면허신고 신청 및 접수 완료 화면';
      if (image.includes('B2-check2')) return '면허신고 신청 완료 안내 문자';
      if (image.includes('B3 - final1')) return '신고현황 조회 화면';
      if (image.includes('B3 - final2')) return '면허신고확인서 양식';
      if (image.includes('C2- write1')) return '보수교육 면제·유예·비대상 신청서 작성 화면';
      if (image.includes('C3 - reason1')) return '보수교육 면제·유예·비대상 불가 사유 내용 확인 화면';
      if (image.includes('C3 - reason2')) return '보수교육 면제·유예·비대상 신청 불가 판정 사유 확인 화면';
      if (image.includes('C3 - reason3')) return '보수교육 면제·유예·비대상 신청내역확인 화면';
      if (image.includes('C3-reason4')) return '보수교육 면제·유예·비대상 확인서 양식';
      if (image.includes('면허업무내용')) return '보건의료정보관리사(의무기록사) 업무';
      if (image.includes('면허업무내용2')) return '보건의료정보관리사(의무기록사) 업무';
      if (image.includes('2014년~2016년 신청 증빙서류_')) return '2014년~2016년 신청 증빙서류';
      if (image.includes('2014년~2016년 신청 증빙서류')) return '2014년~2016년 신청 증빙서류';
      if (image.includes('2017~2018년 증빙서류_비대상자_')) return '2017~2018년 증빙서류_비대상자';
      if (image.includes('2017~2018년 증빙서류_비대상자')) return '2017~2018년 증빙서류_비대상자';
      if (image.includes('2017~2018년 증빙서류_면제유예_')) return '2017~2018년 증빙서류_면제유예';
      if (image.includes('2017~2018년 증빙서류_면제유예001')) return '2017~2018년 증빙서류_면제유예';
      if (image.includes('2017~2018년 증빙서류')) return '2017~2018년 증빙서류';
      if (image.includes('2019년 이후_')) return '2019년 이후';
      if (image.includes('2019년 이후')) return '2019년 이후';
      return null;
    };
    return <div className="flex flex-col items-center gap-8 my-8">
      {images.map((image, index) => {
        const description = getImageDescription(image, index);
        return <div key={index} className="text-center max-w-4xl w-full relative">
          {description && <div className="absolute top-1 left-1 bg-blue-600 text-white px-3 py-1 rounded text-sm font-semibold z-10">{description}</div>}
          <img src={image} alt={`참고 이미지 ${index + 1}`} className="decision-image w-full max-w-none rounded-lg shadow-lg mx-auto" />
        </div>;
      })}
    </div>;
  };
  const handleDownloadFAQ = async () => {
    try {
      const response = await fetch('/보수교육·면허신고 관련 FAQ.pdf');
      if (!response.ok) {
        throw new Error('파일을 찾을 수 없습니다.');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = '보수교육·면허신고 관련 FAQ.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('FAQ 다운로드 오류:', error);
      alert('FAQ 파일 다운로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }
  };

  const renderLinks = (links: {
    text: string;
    url: string;
    download?: boolean;
  }[]) => <div className="my-4 space-y-2">
      {links.map((link, index) => <div key={index} className="flex items-center gap-2">
          {link.download ? (
            <button 
              onClick={handleDownloadFAQ}
              className="external-link cursor-pointer bg-transparent border-none p-0 text-left"
            >
              {link.text}
              <Download className="w-4 h-4" />
            </button>
          ) : (
            <a href={link.url} target="_blank" rel="noopener noreferrer" className="external-link">
              {link.text}
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>)}
    </div>;
  const renderContent = (content: string) => {
    const parts = content.split('\n');
    return parts.map((part, index) => {
      if (part.includes('○') || part.includes('■')) {
        return <div key={index} className="emphasis">{part}</div>;
      }
      if (part.includes('\'') && (part.includes('대상자') || part.includes('면제자') || part.includes('유예자') || part.includes('비대상자'))) {
        return <div key={index} className="highlight font-semibold text-lg">{part}</div>;
      }
      if (part.includes('<strong><u>') && part.includes('</u></strong>')) {
        return <div key={index} dangerouslySetInnerHTML={{
          __html: part
        }} />;
      }
      return <div key={index}>{part}</div>;
    });
  };
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* 헤더 */}
      <header className="gov-gradient text-white py-6 shadow-lg no-print">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="title-responsive font-bold">보수교육·면허신고 절차 안내 도우미</h1>
              <p className="text-blue-100 mt-2">보건의료정보관리사 면허 소지자를 위한 안내</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handlePrint} className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                <Download className="w-4 h-4 mr-2" />
                PDF 저장
              </Button>
            {isAdminMode && <Button variant="outline" size="sm" onClick={onShowAdmin || (() => {})} className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                <Eye className="w-4 h-4 mr-2" />
                관리자 보기
              </Button>}
            </div>
          </div>
        </div>
      </header>

      {/* 메인 콘텐츠 */}
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          {/* 진행 상황 표시 */}
          <div className="flex items-center justify-between mb-6 no-print">
            <div className="flex items-center gap-4">
              <div className="step-indicator active w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold">
                {stepNumber}
              </div>
              <div className="text-sm text-gray-600">
                단계 {stepNumber} / {currentNode.type === 'question' ? '질문' : '결과'}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleHome} disabled={currentNodeId === 'start'}>
                <Home className="w-4 h-4 mr-2" />
                처음으로
              </Button>
              <Button variant="outline" size="sm" onClick={handleBack} disabled={history.length <= 1} className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3 text-[rgb(255,255,255)] bg-[rgb(9,79,156)]">
                <ChevronLeft className="w-4 h-4 mr-2" />
                이전
              </Button>
            </div>
          </div>

          {/* 콘텐츠 카드 */}
          <Card className="gov-card fade-in">
            <CardHeader className="text-center">
              {currentNode.id !== 'start' && <div className="flex items-center justify-center gap-2 mb-2">
                  {currentNode.type === 'question' ? <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                      질문
                    </Badge> : <Badge variant="secondary" className="bg-green-100 text-green-800">
                      결과
                    </Badge>}
                </div>}
              <CardTitle className="title-responsive text-gray-800">
                {currentNode.type === 'question' && currentNode.id !== 'start' ? currentNode.content.split('\n')[0] : currentNode.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* 내용 */}
              <div className="text-responsive text-gray-700 whitespace-pre-line leading-relaxed text-center">
                {currentNode.type === 'question' && currentNode.id !== 'start' ? renderContent(currentNode.content.split('\n').slice(1).join('\n')) : renderContent(currentNode.content)}
              </div>

              {/* 링크 */}
              {currentNode.links && renderLinks(currentNode.links)}

              {/* 테이블 */}
              {currentNode.table && renderTable(currentNode.table)}

              {/* 추가 내용 */}
              {currentNode.content2 && <div className="text-responsive text-gray-700 whitespace-pre-line leading-relaxed text-center">
                  {renderContent(currentNode.content2)}
                </div>}

              {/* 추가 테이블 */}
              {currentNode.table2 && renderTable(currentNode.table2)}

              {/* 이미지 */}
              {currentNode.images && renderImages(currentNode.images)}

              {/* 선택지 또는 다음 버튼 */}
              <div className="flex flex-col gap-3 pt-4">
                {currentNode.options ?
              // 질문의 경우 선택지 표시
              currentNode.options.map((option, index) => <Button key={index} onClick={() => handleOptionClick(option.nextId)} className="gov-button-primary h-16 text-xl font-semibold slide-in px-8" style={{
                animationDelay: `${index * 0.1}s`
              }}>
                      {option.text}
                      <ChevronRight className="w-6 h-6 ml-3" />
                    </Button>) : currentNode.nextId ?
              // 결과의 경우 다음 버튼 표시
              <Button onClick={handleNext} className="gov-button-primary h-16 text-xl font-semibold px-8">
                    다음 단계
                    <ChevronRight className="w-6 h-6 ml-3" />
                  </Button> :
              // 마지막 단계의 경우 처음으로 버튼
              <Button onClick={handleHome} className="gov-button-primary h-16 text-xl font-semibold px-8">
                    처음으로 돌아가기
                    <Home className="w-6 h-6 ml-3" />
                  </Button>}
              </div>
            </CardContent>
          </Card>

          {/* 도움말 */}
          <div className="mt-8 text-center text-sm text-gray-500 no-print">
            <p>추가 문의사항이 있으시면 대한보건의료정보관리사협회로 문의하여 주시기 바랍니다.</p>
            <p className="mt-1">홈페이지: https://www.khima.or.kr/</p>
          </div>
        </div>
      </main>
    </div>;
}